'use client'

import { useMemo, useState } from 'react'

import { PawPrintIcon as Paw } from 'lucide-react'

import { useConfirmedHotelTodayReservations, useReservations } from '@/components/ReservationContext'
import { Alert, AlertDescription } from '@/shared/ui/alert'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Separator } from '@/shared/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { HotelReservation, Pet } from '@monorepo/functions/src/types/reservations'
import { AdditionalService } from '@monorepo/functions/src/types/services'

import { getAllSpecialConditions } from '../model/constants/special-conditions'
import { PetRoomDetails } from './pet-room-details'

interface Room {
    id: string
    number: string
}

interface RoomDetailsModalProps {
    room: Room
    onClose: () => void
}

export interface PetInfo extends Pet {
    id: string
    checkInDate: string
    checkOutDate: string
}

export function RoomDetailsModal({ room, onClose }: RoomDetailsModalProps) {
    const { reservations, updateReservation } = useConfirmedHotelTodayReservations()
    console.log('confirmed reservations', reservations)

    const [syncError, setSyncError] = useState<string>('')

    const petsDetails = useMemo(() => {
        return reservations.flatMap(r =>
            r.pets
                .filter(pet => pet.roomNumber === room.number)
                .map((pet, index) => ({
                    ...pet,
                    id: `${r.id}-${pet.name}-${index}`,
                    checkInDate: r.checkInDate,
                    checkOutDate: r.checkOutDate,
                    conditions: getAllSpecialConditions(r, pet, index),
                })),
        )
    }, [reservations])

    const availablePets = useMemo(() => {
        return reservations.flatMap(r =>
            r.pets
                .filter(pet => pet.roomNumber !== room.number)
                .map((pet, petIndex) => ({
                    ...pet,
                    id: pet.id || `${r.id}-${pet.name}-${petIndex}`,
                })),
        )
    }, [reservations, room.number])

    const handleAddPet = async (petId: string) => {
        try {
            const selectedPet = availablePets.find(p => p.id === petId)
            if (selectedPet) {
                const [reservationId] = petId.split('-')
                const reservation = reservations.find(r => r.id === reservationId)
                if (reservation) {
                    const updatedPets = reservation.pets.map(p =>
                        p.name === selectedPet.name ? { ...p, roomNumber: room.number } : p,
                    )

                    await updateReservation(reservationId, { pets: updatedPets })
                    setSyncError('')
                }
            }
        } catch (error) {
            console.error('Error adding pet to room:', error)
            setSyncError('Error al añadir la mascota a la habitación. Por favor, inténtelo de nuevo.')
        }
    }

    const handlePetChange = async (petId: string, petChanges: Partial<Pet>) => {
        try {
            const [reservationId, petName] = petId.split('-')
            const reservation = reservations.find(r => r.id === reservationId)

            if (reservation) {
                const updatedPets = reservation.pets.map(p => (p.name === petName ? { ...p, ...petChanges } : p))

                await updateReservation(reservationId, { pets: updatedPets })
                setSyncError('')
            }
        } catch (error) {
            console.error('Error updating pet:', error)
            setSyncError('Error al actualizar la mascota. Por favor, inténtelo de nuevo.')
        }
    }

    const handleRoomChange = async (petId: string, newRoomNumber: string) => {
        try {
            const [reservationId, petName] = petId.split('-')
            const reservation = reservations.find(r => r.id === reservationId)

            if (reservation) {
                const updatedPets = reservation.pets.map(p =>
                    p.name === petName ? { ...p, roomNumber: newRoomNumber } : p,
                )

                await updateReservation(reservationId, { pets: updatedPets })
                setSyncError('')
            }
        } catch (error) {
            console.error('Error changing pet room:', error)
            setSyncError('Error al cambiar la habitación de la mascota. Por favor, inténtelo de nuevo.')
        }
    }

    const handleServiceChange = async (services: AdditionalService[]) => {
        try {
            // Find the reservation that contains the services being updated
            const reservation = reservations.find(r =>
                r.additionalServices.some(service =>
                    services.some(newService => {
                        // Check if it's a service with petIndex (not driver service)
                        if ('petIndex' in service && 'petIndex' in newService) {
                            return newService.petIndex === service.petIndex && newService.type === service.type
                        }
                        return newService.type === service.type
                    }),
                ),
            )

            if (reservation) {
                await updateReservation(reservation.id, { additionalServices: services })
                setSyncError('')
            }
        } catch (error) {
            console.error('Error updating services:', error)
            setSyncError('Error al actualizar los servicios. Por favor, inténtelo de nuevo.')
        }
    }

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className='max-h-[90vh] max-w-4xl gap-1 overflow-y-auto'>
                <DialogHeader className='flex flex-row items-center justify-between'>
                    <DialogTitle className='text-2xl font-semibold text-gray-900'>Habitación {room.number}</DialogTitle>
                </DialogHeader>
                <DialogDescription>Gestiona los detalles de las mascotas en esta habitación.</DialogDescription>
                <Separator className='my-4' />
                <div className='py-4'>
                    {petsDetails.length === 0 ? (
                        <>
                            <Select onValueChange={handleAddPet}>
                                <SelectTrigger className='mb-4 w-fit'>
                                    <SelectValue placeholder='Añadir mascota' />
                                </SelectTrigger>
                                <SelectContent>
                                    {availablePets.map(pet => (
                                        <SelectItem key={pet.id} value={pet.id}>
                                            {pet.name} {pet.roomNumber ? `(${pet.roomNumber})` : '(Sin asignar)'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Alert variant='info'>
                                <AlertDescription>No hay mascotas asignadas a esta habitación.</AlertDescription>
                            </Alert>
                        </>
                    ) : (
                        <>
                            <Tabs defaultValue={petsDetails[0].id}>
                                <div className='flex flex-row items-center justify-start'>
                                    <TabsList>
                                        {petsDetails.map(pet => (
                                            <TabsTrigger key={pet.id} value={pet.id}>
                                                <Paw className='mr-2 h-4 w-4' />
                                                {pet.name}
                                            </TabsTrigger>
                                        ))}
                                    </TabsList>
                                    <Select onValueChange={handleAddPet}>
                                        <SelectTrigger className='ml-2 w-fit'>
                                            <SelectValue placeholder='Añadir mascota' />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availablePets.map(pet => (
                                                <SelectItem key={pet.id} value={pet.id}>
                                                    {pet.name}{' '}
                                                    {pet.roomNumber ? `(${pet.roomNumber})` : '(Sin asignar)'}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {petsDetails.map(pet => (
                                    <TabsContent key={pet.id} value={pet.id} className='mt-4'>
                                        <PetRoomDetails
                                            reservationId={
                                                reservations.find(
                                                    r => r.id === pet.id.split('-')[0],
                                                ) as HotelReservation
                                            }
                                            pet={pet}
                                            onPetChange={handlePetChange}
                                            onRoomChange={handleRoomChange}
                                            onServiceChange={handleServiceChange}
                                            reservations={reservations}
                                            onClose={onClose}
                                        />
                                    </TabsContent>
                                ))}
                            </Tabs>
                        </>
                    )}
                </div>
                {syncError && (
                    <Alert variant='destructive'>
                        <AlertDescription>{syncError}</AlertDescription>
                    </Alert>
                )}
            </DialogContent>
        </Dialog>
    )
}
